import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { mergeMap, map, catchError, withLatestFrom, filter, switchMap } from 'rxjs/operators';
import * as CustomerActions from '../actions/customer.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.state';
import { SpotsService } from '../services/spots.service';
import { Customer } from '../models/customer.model';
import * as UiActions from '../../ui/actions/ui.actions';

@Injectable()
export class CustomerEffects {
    constructor(
        private actions$: Actions,
        private spotsService: SpotsService,
        private store: Store<AppState>
    ) { }

    loadCustomers$ = createEffect(() => this.actions$.pipe(
        ofType(CustomerActions.loadCustomers),
        withLatestFrom(this.store.select(state => state.spots.customers)),
        filter(([action, customers]) => !customers || customers.length === 0), // only continue if it was not loaded before
        mergeMap(([action, customers]) => {
            const { organizationId } = action; // Extract the organizationId from the action

            return this.spotsService.getCustomers(organizationId).pipe(
                map((customers: Customer[]) => {
                    return CustomerActions.loadCustomersSuccess({ customers });
                }),
                catchError(error => {
                    return of(UiActions.serviceCallFailure({ action: CustomerActions.loadCustomers, error }))
                })
            )
        })
    ));

    addCustomer$ = createEffect(() => this.actions$.pipe(
        ofType(CustomerActions.addCustomer),
        mergeMap(({ organizationId, customer }) => this.spotsService.addCustomer(organizationId, customer).pipe(
            map((customer: Customer) => {
                return CustomerActions.addCustomerSuccess({ customer });
            }),
            catchError(error => {
                return of(UiActions.serviceCallFailure({ action: CustomerActions.addCustomer, error }))
            })
        ))
    ));

    updateCustomer$ = createEffect(() => this.actions$.pipe(
        ofType(CustomerActions.updateCustomer),
        mergeMap(({ organizationId, customerId, customer }) => this.spotsService.updateCustomer(organizationId, customerId, customer).pipe(
            map((customer: Customer) => {
                return CustomerActions.updateCustomerSuccess({ customer });
            }),
            catchError(error => {
                return of(UiActions.serviceCallFailure({ action: CustomerActions.updateCustomer, error }))
            })
        ))
    ));

    deleteCustomer$ = createEffect(() => this.actions$.pipe(
        ofType(CustomerActions.deleteCustomer),
        mergeMap(({ organizationId, customerId }) => this.spotsService.deleteCustomer(organizationId, customerId).pipe(
            map(() => {
                return CustomerActions.deleteCustomerSuccess({ customerId });
            }),
            catchError(error => {
                return of(UiActions.serviceCallFailure({ action: CustomerActions.deleteCustomer, error }))
            })
        ))
    ));

    // FedEx AVR Effects (now part of customer management)
    updateFedexAvr$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.updateFedexAvr),
            switchMap(({ organizationId, key, fedexAvr }) => {
                return this.spotsService.updateFedexAvr(organizationId, key, fedexAvr).pipe(
                    map(() => {
                        // Return the updated AVR with the key for proper update
                        return CustomerActions.updateFedexAvrSuccess({ fedexAvr, key });
                    }),
                    catchError(error => of(UiActions.serviceCallFailure({ action: CustomerActions.updateFedexAvr, error })))
                )
            })
        )
    );
}
